/**
 * TransferOwnershipModal — "Transfer ownership" (wireframe admin-console-wireframe; Ownership design, part 4): "Move N
 * JDs from A, B:", the JDs, the New owner dropdown over the whole HR pool (greyed out with the reason when someone can't
 * take them) with "X of Y people can take these JDs", the one "Existing shares and delegations" choice (Carry them over
 * preselected; the same answer goes as carry_delegation and carry_share), the permanence banner, "Transfer N JDs".
 *   open, rows (the selected JDs), candidates (GET /ownership/transfer_candidates), loadingCandidates, transferring
 *   onCancel(), onTransfer({ newOwner, carry })   carry: true to carry shares and delegations over
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { SbButton, SbModal, SbTooltip, TooltipTriggerStyled } from 'uiCore';
import {
  CARRY, CHOOSE_OWNER, NEW_OWNER_HELP, TRANSFER_TITLE, countPeople, getAffectedNames, getEligibleHelp, getMoveIntro,
  getOwnerNames, getPermanenceBanner, getTransferButton,
} from 'Utils/ownershipTransfer';
import { TransferDescription } from 'Pages/PeoplePlusAIV2/dashboard/ManageGroupsModal.styles';
import {
  ConflictItemMetaStyled, ConflictProjectItemStyled, ConflictProjectListStyled, ConflictProjectNameStyled,
} from 'Components/EmployeeFunctionAccess/EmployeeFunctionAccess.styles';
import { CarryChoice, NewOwnerField, PermanenceBanner } from './TransferParts';

const SHOWN = 5;

const TransferOwnershipModal = ({
  open, rows = [], candidates = null, loadingCandidates = false, candidatesError = null, transferring = false, onCancel,
  onTransfer,
}) => {
  const [newOwner, setNewOwner] = useState(null);
  const [carry, setCarry] = useState(CARRY);
  useEffect(() => {
    if (!open) return;
    setNewOwner(null);
    setCarry(CARRY);
  }, [open]);

  const n = rows.length;
  const owners = getOwnerNames(rows);
  const { ok, total } = countPeople(candidates);
  return (
    <SbModal
      open={open}
      title={TRANSFER_TITLE}
      onCancel={transferring ? undefined : onCancel}
      maskClosable={false}
      destroyOnClose
      width={620}
      footer={[
        <SbButton key="cancel" buttonType="secondary" disabled={transferring} onClick={onCancel}>Cancel</SbButton>,
        <SbTooltip key="ok" content={CHOOSE_OWNER} disabled={!!newOwner}>
          <TooltipTriggerStyled>
            <SbButton buttonType="primary" disabled={!newOwner || transferring} loading={transferring}
              onClick={() => onTransfer({ newOwner, carry: carry === CARRY })}>
              {getTransferButton(n)}
            </SbButton>
          </TooltipTriggerStyled>
        </SbTooltip>,
      ]}
    >
      <TransferDescription>{getMoveIntro(n, owners)}</TransferDescription>
      <ConflictProjectListStyled data-testid="transfer-items">
        {rows.slice(0, SHOWN).map((r) => (
          <ConflictProjectItemStyled key={r.job_id}>
            <ConflictProjectNameStyled>{r.title || r.sb_requisition_number}</ConflictProjectNameStyled>
            <ConflictItemMetaStyled>{r.sb_requisition_number || r.job_id}</ConflictItemMetaStyled>
          </ConflictProjectItemStyled>
        ))}
        {n > SHOWN && <ConflictProjectItemStyled>and {n - SHOWN} more</ConflictProjectItemStyled>}
      </ConflictProjectListStyled>
      <NewOwnerField candidates={candidates} loading={loadingCandidates} value={newOwner} onChange={setNewOwner}
        disabled={transferring} error={candidatesError} helpAbove={NEW_OWNER_HELP} helpBelow={candidates ? getEligibleHelp(ok, total, n) : null} />
      <CarryChoice value={carry} onChange={setCarry} affectedNames={getAffectedNames(rows)} />
      <PermanenceBanner text={getPermanenceBanner(owners, n)} />
    </SbModal>
  );
};

TransferOwnershipModal.propTypes = {
  open: PropTypes.bool.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  candidates: PropTypes.shape({ candidates: PropTypes.array, excluded: PropTypes.array, total: PropTypes.number }),
  loadingCandidates: PropTypes.bool,
  candidatesError: PropTypes.string,
  transferring: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
};

export default TransferOwnershipModal;
